import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { authMiddleware } from "@/routes/-middlewares";

// ---------------------------------------------------------------------------
// Course CRUD
// ---------------------------------------------------------------------------

export const $createCourse = createServerFn({ method: "POST" })
	.middleware([authMiddleware])
	.inputValidator(
		z.object({
			title: z.string().min(1),
			description: z.string().optional(),
		}),
	)
	.handler(async ({ context, data }) => {
		return context.controllers.courseController.createCourse(data.title, data.description);
	});

export const $listCourses = createServerFn({ method: "GET" })
	.middleware([authMiddleware])
	.handler(async ({ context }) => {
		return context.controllers.courseController.listCourses();
	});

export const $getCourse = createServerFn({ method: "GET" })
	.middleware([authMiddleware])
	.inputValidator(z.object({ courseId: z.string() }))
	.handler(async ({ context, data }) => {
		return context.controllers.courseController.getCourse(data.courseId);
	});

export const $updateCourse = createServerFn({ method: "POST" })
	.middleware([authMiddleware])
	.inputValidator(
		z.object({
			courseId: z.string(),
			title: z.string().min(1).optional(),
			description: z.string().nullish(),
			coverImage: z.string().nullish(),
			visibility: z.enum(["private", "public", "organization", "team"]).optional(),
		}),
	)
	.handler(async ({ context, data }) => {
		const { courseId, ...rest } = data;
		return context.controllers.courseController.updateCourse(courseId, rest);
	});

export const $deleteCourse = createServerFn({ method: "POST" })
	.middleware([authMiddleware])
	.inputValidator(z.object({ courseId: z.string() }))
	.handler(async ({ context, data }) => {
		await context.controllers.courseController.deleteCourse(data.courseId);
		return { success: true };
	});

// ---------------------------------------------------------------------------
// Tree
// ---------------------------------------------------------------------------

export const $getCourseTree = createServerFn({ method: "GET" })
	.middleware([authMiddleware])
	.inputValidator(z.object({ courseId: z.string() }))
	.handler(async ({ context, data }) => {
		return context.controllers.courseController.getFullTree(data.courseId);
	});

// ---------------------------------------------------------------------------
// Node CRUD
// ---------------------------------------------------------------------------

export const $addCourseNode = createServerFn({ method: "POST" })
	.middleware([authMiddleware])
	.inputValidator(
		z.object({
			courseId: z.string(),
			parentId: z.string().nullish(),
			title: z.string().min(1),
			versionId: z.string().nullish(),
		}),
	)
	.handler(async ({ context, data }) => {
		return context.controllers.courseController.addNode(data.courseId, {
			parentId: data.parentId ?? null,
			title: data.title,
			versionId: data.versionId ?? null,
		});
	});

export const $updateCourseNode = createServerFn({ method: "POST" })
	.middleware([authMiddleware])
	.inputValidator(
		z.object({
			nodeId: z.string(),
			title: z.string().min(1).optional(),
			description: z.string().nullish(),
			versionId: z.string().nullish(),
		}),
	)
	.handler(async ({ context, data }) => {
		const { nodeId, ...rest } = data;
		return context.controllers.courseController.updateNode(nodeId, rest);
	});

export const $deleteCourseNode = createServerFn({ method: "POST" })
	.middleware([authMiddleware])
	.inputValidator(z.object({ nodeId: z.string() }))
	.handler(async ({ context, data }) => {
		await context.controllers.courseController.deleteNode(data.nodeId);
		return { success: true };
	});

export const $moveCourseNode = createServerFn({ method: "POST" })
	.middleware([authMiddleware])
	.inputValidator(
		z.object({
			nodeId: z.string(),
			newParentId: z.string().nullish(),
			newPosition: z.number().int().min(0),
		}),
	)
	.handler(async ({ context, data }) => {
		await context.controllers.courseController.moveNode(data.nodeId, {
			newParentId: data.newParentId ?? null,
			newPosition: data.newPosition,
		});
		return { success: true };
	});

export const $reorderCourseNodes = createServerFn({ method: "POST" })
	.middleware([authMiddleware])
	.inputValidator(
		z.object({
			courseId: z.string(),
			parentId: z.string().nullish(),
			orderedNodeIds: z.array(z.string()),
		}),
	)
	.handler(async ({ context, data }) => {
		await context.controllers.courseController.reorderNodes(
			data.courseId,
			data.parentId ?? null,
			data.orderedNodeIds,
		);
		return { success: true };
	});
